import {
	clampTimeOfDay,
	GOLDEN_HOUR_TIME_OF_DAY,
	type LegacyTimeOfDay,
	NIGHT_TIME_OF_DAY,
	normalizeTimeOfDay,
	type TimeOfDayValue,
} from "../../domain/viewer/timeOfDay";

export type ViewerQualityTier = "auto" | "high" | "balanced" | "low";

/**
 * Exposure compensation applied on top of the per-tier
 * `toneMappingExposure` now that the renderer uses AgX instead of
 * ACES filmic. AgX has a gentler shoulder and reads ~15–20% darker
 * at equal exposure; compensating here (rather than rewriting every
 * tier/evening exposure value) keeps the artistic exposure numbers
 * comparable across both tonemappers. Applied wherever
 * `toneMappingExposure` is written to the renderer
 * (RenderController initial set + SceneLighting transition lerp).
 */
export const TONE_MAPPING_EXPOSURE_COMPENSATION = 1.18;

/**
 * AgX also desaturates strong colours compared to ACES. The grade
 * pass adds this flat saturation lift back so warm wood tones keep
 * their depth. Only applies on tiers that run the grade pass.
 */
export const AGX_SATURATION_COMPENSATION = 0.055;
export type TimeOfDay = TimeOfDayValue | LegacyTimeOfDay;
export type { LegacyTimeOfDay, TimeOfDayValue };
export type ViewMode = "realistic" | "blueprint";
export type PresentationMode = "standard" | "cinematic";
export const premiumModulesEnabled = true;

export type ResolvedViewerQualityTier = Exclude<ViewerQualityTier, "auto">;

export interface ViewerPerformanceSample {
	factor: number;
	fps: number;
	refreshrate: number;
	fallback: boolean;
}

export interface AdaptiveViewerQualityState {
	baseTier: ResolvedViewerQualityTier;
	tier: ResolvedViewerQualityTier;
}

export interface ViewerRuntimeProfile {
	tier: ResolvedViewerQualityTier;
	presentationMode: PresentationMode;
	pixelRatioCap: number;
	/** Explicit composer MSAA budget; never inherit the library's 8x default. */
	postProcessingMultisampling: number;
	shadowMapSize: number;
	ssaoEnabled: boolean;
	ssaoKernelRadius: number;
	ssaoMinDistance: number;
	ssaoMaxDistance: number;
	bloomEnabled: boolean;
	bloomIntensity: number;
	/**
	 * Luminance above which bloom kicks in. Lower values let mid-tone
	 * highlights (sun on door handles, eaves, glass edges) pick up a
	 * subtle glow. 0.6–0.7 reads as architectural; 0.85+ is "only the
	 * brightest emissives," which can leave the rest of the model flat.
	 */
	bloomThreshold: number;
	/**
	 * Use PCFSoftShadowMap instead of plain PCFShadowMap. Softer
	 * silhouette edges, slightly more expensive. Reserved for high tier
	 * where the GPU can spare the samples.
	 */
	softShadows: boolean;
	/**
	 * Load the real HDRI environment map (Polyhaven .hdr in
	 * public/assets/hdri/). When false, the procedural DataTexture
	 * envMap is used — faster start, no extra fetch, no asset budget.
	 * The procedural map always renders first; the real HDRI swaps
	 * in once decoded so balanced/high tiers never wait for it.
	 */
	useRealHdri: boolean;
	/** Entrance stepping stones; forest remains visible on every quality tier. */
	dressingEnabled: boolean;
	/**
	 * Depth-of-field for the explicit Cinematic presentation. Standard
	 * viewing never changes render path merely because the camera rests.
	 */
	depthOfFieldEnabled: boolean;
	/**
	 * Render the instanced grass blades around the sauna footprint.
	 * Enabled only on `high` because the wind animation runs whenever
	 * any frame is invalidated — its cost rises with `grassDensity`.
	 * The animation does NOT call `invalidate()` itself, so when the
	 * camera is still the blades freeze instead of pinning the
	 * frameloop (per demand-mode-viewers-must-gate-idle-ambience-loops).
	 */
	instancedGrassEnabled: boolean;
	/**
	 * Number of grass-blade instances scattered around the sauna.
	 * Reads as "lush" at 12 000+, "tasteful" at ~7 000. Each blade is
	 * a 1-triangle billboard (~3 verts), so cost is dominated by
	 * matrix uploads and shader cost rather than geometry.
	 */
	grassDensity: number;
	toneMappingExposure: number;
	ambientIntensity: number;
	hemisphereIntensity: number;
	keyIntensity: number;
	fillIntensity: number;
	rimIntensity: number;
	interiorGlowIntensity: number;
	keyPosition: [number, number, number];
	fillPosition: [number, number, number];
	rimPosition: [number, number, number];
	interiorGlowPosition: [number, number, number];
	contactShadowStrength: number;
	reflectionBoost: number;
	gradeWarmth: number;
	gradeContrast: number;
	steamEnabled: boolean;
	heaterAnimationEnabled: boolean;
	/**
	 * Use the physically-based transmission glass (refraction through
	 * `MeshPhysicalMaterial.transmission`) instead of the opacity
	 * fallback. Any transmissive object in the render list makes
	 * three.js render the whole scene an extra time into the
	 * transmission render target, so this is one of the most expensive
	 * single flags in the profile — with the two-room panorama walls
	 * it can mean four large glass surfaces paying that pass.
	 * Reserved for explicit Cinematic mode (except on `low`, where
	 * Cinematic is a no-op). Standard tiers swap glass
	 * meshes to the alpha-blended budget materials instead — see
	 * `applyGlassQualityMode` in src/engines/materials.ts.
	 */
	physicalGlassEnabled: boolean;
	/**
	 * Upgrade interior panel and bench surfaces to MeshPhysicalMaterial
	 * variants with anisotropic/clearcoat highlights. This adds shader
	 * cost across many wood meshes, so it is reserved for high tier and
	 * is not forced by cinematic mode on balanced hardware.
	 */
	physicalWoodSurfacesEnabled: boolean;
	/**
	 * Render a per-frame baked contact-shadow plane under the model
	 * via drei's `<ContactShadows>`. This is a real shadow render
	 * (orthographic camera + depth pass) rather than the static
	 * `createGroundOverlayTexture` halo and reads as physical contact
	 * between the sauna and the ground. Off on `low` to spare the
	 * extra render target; on for `balanced` and up.
	 */
	contactShadowsEnabled: boolean;
	/**
	 * Render the evening "god ray" light cones spilling outward from
	 * glazed openings (`<LightShafts>`). Cheap fake volumetrics — static
	 * additive geometry, no per-frame cost on the demand frameloop — but
	 * still an extra few draw calls and a transparent additive pass, so
	 * it is gated to `balanced`+ and off on `low`. The cones only render
	 * inside the golden-hour→night band; daytime returns nothing.
	 */
	lightShaftsEnabled: boolean;
	/** Render-target resolution for the contact shadow pass. */
	contactShadowResolution: number;
	/** Blur radius for the contact shadow pass (drei units). */
	contactShadowBlur: number;
	/** Opacity of the contact shadow pass. */
	contactShadowOpacity: number;
	/**
	 * Render stencil-based colour caps on the section-view cut faces.
	 * Clipping itself is cheap and runs on every tier; the per-family
	 * stencil caps add extra draw calls and need a stencil-backed
	 * composer buffer, so they are gated to `balanced`+ (off on `low`).
	 * The cut still reads as filled on `low` via double-sided clipping.
	 */
	sectionCapsEnabled: boolean;
	/**
	 * Keep the current lakeside composition on every quality tier. Only
	 * reflection fidelity changes with quality; switching to low must not
	 * replace the lake with dry ground. A future site selector may own presence.
	 */
	waterEnabled: boolean;
	/**
	 * Use a real planar reflection (drei `MeshReflectorMaterial`) for the
	 * water so the sauna itself mirrors in the surface. Like the
	 * transmission-glass pass, this re-renders the scene from a mirrored
	 * camera into an FBO, so it is one of the heavier flags — reserved for
	 * `high` tier. `balanced` keeps a cheap glossy plane that reflects only
	 * the environment map (sky + sun), which is demand-mode free. Cinematic
	 * forces it on wherever water is present, because the sauna mirrored in
	 * a still lake is exactly the marketing-screenshot shot.
	 */
	waterReflectionsEnabled: boolean;
	/**
	 * Full procedural lake: nine ripple waves, three pebble sizes, moving
	 * caustics and cellular foam. Every water pixel pays for it, so `low`
	 * draws the lite variant (four waves, one pebble size, no caustics,
	 * banded foam) of the same shader. See lakeWaterShader.ts.
	 */
	waterDetailEnabled: boolean;
	/**
	 * Peak amplitude of the static film grain in the grade (0 = off). A
	 * merged effect, so it adds no pass; it only runs where the grade
	 * itself does (`balanced`+). See filmGrainEffect.ts.
	 */
	filmGrainStrength: number;
	/**
	 * Faint radial chromatic aberration toward the frame edges, the lens
	 * cue that separates a photograph from a render. A merged effect but
	 * with three texture reads per pixel, so `high` and Cinematic only.
	 */
	chromaticAberrationEnabled: boolean;
	/**
	 * A thin plume of smoke from the chimney of a wood-fired heater: a
	 * dozen soft billboards, no extra pass. Animates only while the
	 * viewer is already repainting (see ambientEffectClock.ts).
	 */
	chimneySmokeEnabled: boolean;
	/**
	 * Dust motes drifting inside the evening light shafts. A few hundred
	 * additive points per glazed opening; rides `lightShaftsEnabled`, so
	 * it can never render on a tier without the shafts themselves.
	 */
	shaftDustEnabled: boolean;
}

export interface ViewerPresentationProfile {
	environment: "studio-soft";
	backgroundTop: number;
	backgroundHorizon: number;
	backgroundBottom: number;
	backgroundAccent: number;
	vignetteStrength: number;
	highlightStrength: number;
	ambientIntensity: number;
	keyIntensity: number;
}

export function resolveViewerQualityTier({
	devicePixelRatio,
	viewportWidth,
	viewportHeight,
	override = "auto",
}: {
	devicePixelRatio: number;
	viewportWidth: number;
	viewportHeight: number;
	override?: ViewerQualityTier;
}): ResolvedViewerQualityTier {
	if (override !== "auto") return override;
	const shortEdge = Math.min(viewportWidth, viewportHeight);
	const longEdge = Math.max(viewportWidth, viewportHeight);

	if (
		shortEdge < 760 ||
		devicePixelRatio >= 2.4 ||
		(viewportWidth < 960 && devicePixelRatio > 1.7)
	) {
		return "low";
	}
	if (longEdge >= 1680 && shortEdge >= 960 && devicePixelRatio <= 1.6) {
		return "high";
	}
	return "balanced";
}

export function getViewerRuntimeProfile(
	tier: ResolvedViewerQualityTier,
): ViewerRuntimeProfile {
	if (tier === "high") {
		return {
			tier,
			presentationMode: "standard",
			pixelRatioCap: 1.6,
			postProcessingMultisampling: 2,
			shadowMapSize: 2560,
			ssaoEnabled: true,
			ssaoKernelRadius: 9,
			ssaoMinDistance: 0.01,
			ssaoMaxDistance: 0.075,
			bloomEnabled: true,
			// Threshold raised from 0.62 — at the lower value the
			// real HDRI was driving cream interior aspen above the
			// bloom cutoff and the inside read as blown-out under
			// strong key light. 0.72 still picks up sun on metal /
			// glass edges.
			bloomIntensity: 0.18,
			bloomThreshold: 0.72,
			softShadows: true,
			useRealHdri: true,
			dressingEnabled: true,
			depthOfFieldEnabled: false,
			instancedGrassEnabled: true,
			grassDensity: 9000,
			toneMappingExposure: 1.06,
			ambientIntensity: 0.1,
			hemisphereIntensity: 0.28,
			keyIntensity: 2.65,
			fillIntensity: 0.82,
			rimIntensity: 0.58,
			interiorGlowIntensity: 0.3,
			keyPosition: [7, 8, 8],
			fillPosition: [-6, 5, 5],
			rimPosition: [-4, 6, -7],
			interiorGlowPosition: [0.4, 1.4, 0.9],
			contactShadowStrength: 0.78,
			reflectionBoost: 1.18,
			gradeWarmth: 0.09,
			gradeContrast: 0.12,
			steamEnabled: true,
			heaterAnimationEnabled: true,
			physicalGlassEnabled: false,
			physicalWoodSurfacesEnabled: true,
			contactShadowsEnabled: true,
			lightShaftsEnabled: true,
			contactShadowResolution: 1024,
			contactShadowBlur: 2.4,
			contactShadowOpacity: 0.46,
			sectionCapsEnabled: true,
			waterEnabled: true,
			// Perf: the planar reflection (`MeshReflectorMaterial`) re-renders
			// the WHOLE scene from a mirror camera every frame the camera
			// moves — a full extra ~250-draw pass that measured ~30% of the
			// per-frame cost during orbit. A calm lake reads fine with the
			// cheap glossy env-map water, so the true sauna-mirror is reserved
			// for cinematic mode (the marketing screenshot, where frame rate
			// doesn't matter). `applyCinematicPresentation` turns it back on.
			waterReflectionsEnabled: false,
			waterDetailEnabled: true,
			filmGrainStrength: 0.03,
			chromaticAberrationEnabled: true,
			chimneySmokeEnabled: true,
			shaftDustEnabled: true,
		};
	}
	if (tier === "low") {
		return {
			tier,
			presentationMode: "standard",
			pixelRatioCap: 1.1,
			postProcessingMultisampling: 0,
			shadowMapSize: 768,
			ssaoEnabled: false,
			ssaoKernelRadius: 0,
			ssaoMinDistance: 0.01,
			ssaoMaxDistance: 0.06,
			bloomEnabled: false,
			bloomIntensity: 0,
			bloomThreshold: 0.85,
			softShadows: false,
			useRealHdri: false,
			dressingEnabled: false,
			depthOfFieldEnabled: false,
			instancedGrassEnabled: false,
			grassDensity: 0,
			// Shape comes from the existing directional lights, even without AO
			// or a photographic environment. Keep low's pass and texture budget.
			toneMappingExposure: 1.04,
			ambientIntensity: 0.1,
			hemisphereIntensity: 0.3,
			keyIntensity: 2.3,
			fillIntensity: 0.74,
			rimIntensity: 0.34,
			interiorGlowIntensity: 0.22,
			keyPosition: [5.5, 7, 6.5],
			fillPosition: [-5, 4, 4],
			rimPosition: [-2.5, 4.5, -6],
			interiorGlowPosition: [0.25, 1.15, 0.65],
			contactShadowStrength: 0.12,
			reflectionBoost: 0.94,
			gradeWarmth: 0.02,
			gradeContrast: 0.03,
			steamEnabled: false,
			heaterAnimationEnabled: false,
			physicalGlassEnabled: false,
			physicalWoodSurfacesEnabled: false,
			contactShadowsEnabled: false,
			lightShaftsEnabled: false,
			contactShadowResolution: 0,
			contactShadowBlur: 0,
			contactShadowOpacity: 0,
			sectionCapsEnabled: false,
			waterEnabled: true,
			waterReflectionsEnabled: false,
			waterDetailEnabled: false,
			filmGrainStrength: 0,
			chromaticAberrationEnabled: false,
			chimneySmokeEnabled: false,
			shaftDustEnabled: false,
		};
	}
	return {
		tier,
		presentationMode: "standard",
		pixelRatioCap: 1.3,
		postProcessingMultisampling: 0,
		shadowMapSize: 1280,
		ssaoEnabled: true,
		ssaoKernelRadius: 5,
		ssaoMinDistance: 0.012,
		ssaoMaxDistance: 0.068,
		bloomEnabled: true,
		bloomIntensity: 0.13,
		bloomThreshold: 0.78,
		softShadows: false,
		useRealHdri: true,
		dressingEnabled: true,
		depthOfFieldEnabled: false,
		instancedGrassEnabled: false,
		grassDensity: 0,
		// Match the low/high daylight identity while keeping balanced's
		// render targets, resolution and HDRI budget unchanged.
		toneMappingExposure: 1.05,
		ambientIntensity: 0.1,
		hemisphereIntensity: 0.28,
		keyIntensity: 2.45,
		fillIntensity: 0.78,
		rimIntensity: 0.48,
		interiorGlowIntensity: 0.26,
		keyPosition: [6.2, 7.5, 7.2],
		fillPosition: [-5.4, 4.6, 4.6],
		rimPosition: [-3.2, 5.2, -6.5],
		interiorGlowPosition: [0.3, 1.25, 0.78],
		contactShadowStrength: 0.48,
		reflectionBoost: 1.08,
		gradeWarmth: 0.07,
		gradeContrast: 0.08,
		steamEnabled: true,
		heaterAnimationEnabled: true,
		// Balanced keeps the opacity-glass fallback: the transmission
		// pass re-renders the scene once more per frame, which is the
		// kind of cliff the retina-laptop tier exists to avoid.
		physicalGlassEnabled: false,
		physicalWoodSurfacesEnabled: false,
		contactShadowsEnabled: true,
		lightShaftsEnabled: true,
		contactShadowResolution: 768,
		contactShadowBlur: 2.1,
		contactShadowOpacity: 0.38,
		sectionCapsEnabled: true,
		waterEnabled: true,
		// Balanced keeps the env-map-only glossy water: a planar reflection
		// pass re-renders the whole scene, the same cliff the retina-laptop
		// tier exists to avoid. The lake still reflects sky + sun.
		waterReflectionsEnabled: false,
		waterDetailEnabled: true,
		filmGrainStrength: 0.026,
		chromaticAberrationEnabled: false,
		chimneySmokeEnabled: true,
		shaftDustEnabled: true,
	};
}

/**
 * Apply cinematic-mode boosts on top of a base tier profile.
 *
 * Cinematic is a presentation toggle separate from the quality tier:
 * it tells the renderer "stop optimising for interactivity, optimise
 * for the marketing screenshot." Bumps tone-mapping exposure, forces
 * soft shadows, doubles the contact-shadow opacity, fattens the AO
 * radius and enables DoF explicitly so the composed image reads as
 * architectural-photo bokeh.
 *
 * Returns a NEW profile — the input is not mutated. Tier is preserved
 * so the adaptive perf monitor and any tier-conditional code paths
 * continue to read the underlying tier.
 */
export function applyCinematicPresentation(
	base: ViewerRuntimeProfile,
): ViewerRuntimeProfile {
	if (base.tier === "low") {
		// On low tier we cannot afford the extra render targets.
		// Cinematic toggle is a no-op rather than risking a crash.
		return base;
	}
	return {
		...base,
		presentationMode: "cinematic",
		toneMappingExposure: base.toneMappingExposure + 0.08,
		shadowMapSize: Math.min(base.shadowMapSize * 2, 4096),
		softShadows: true,
		bloomEnabled: true,
		bloomIntensity: base.bloomIntensity * 1.35,
		bloomThreshold: Math.max(base.bloomThreshold - 0.04, 0.6),
		ssaoEnabled: true,
		ssaoKernelRadius: Math.max(base.ssaoKernelRadius, 7) * 1.25,
		ssaoMaxDistance: Math.max(base.ssaoMaxDistance, 0.085),
		depthOfFieldEnabled: true,
		gradeWarmth: base.gradeWarmth + 0.04,
		gradeContrast: base.gradeContrast + 0.05,
		contactShadowsEnabled: true,
		contactShadowResolution: Math.max(base.contactShadowResolution, 1024),
		contactShadowBlur: Math.max(base.contactShadowBlur, 2.6) * 1.15,
		contactShadowOpacity: Math.min(
			Math.max(base.contactShadowOpacity, 0.42) * 1.35,
			0.85,
		),
		contactShadowStrength: Math.min(base.contactShadowStrength * 1.2, 1.0),
		reflectionBoost: base.reflectionBoost * 1.06,
		// The marketing screenshot is exactly where real refraction
		// through the glazing pays off — kvällsglöd through transmissive
		// glass is the signature shot. Low tier never reaches this line.
		physicalGlassEnabled: true,
		// Upgrade the lake to a true planar reflection wherever water is
		// present. The sauna mirrored in a still lake is the hero shot;
		// cinematic accepts the extra reflection pass for it.
		waterReflectionsEnabled: base.waterEnabled,
		filmGrainStrength: Math.max(base.filmGrainStrength, 0.03) * 1.2,
		chromaticAberrationEnabled: true,
	};
}

const qualityTierOrder: ResolvedViewerQualityTier[] = [
	"low",
	"balanced",
	"high",
];

export function getLowerViewerQualityTier(
	tier: ResolvedViewerQualityTier,
): ResolvedViewerQualityTier {
	const index = qualityTierOrder.indexOf(tier);
	return qualityTierOrder[Math.max(0, index - 1)];
}

export function getHigherViewerQualityTier(
	tier: ResolvedViewerQualityTier,
): ResolvedViewerQualityTier {
	const index = qualityTierOrder.indexOf(tier);
	return qualityTierOrder[Math.min(qualityTierOrder.length - 1, index + 1)];
}

export function resolveAdaptiveViewerQualityTier({
	baseTier,
	currentTier,
	performance,
}: {
	baseTier: ResolvedViewerQualityTier;
	currentTier: ResolvedViewerQualityTier | null;
	performance: ViewerPerformanceSample;
}): ResolvedViewerQualityTier {
	if (performance.fallback || performance.factor <= 0.2) return "low";

	const tier = currentTier ?? baseTier;
	if (performance.factor <= 0.4) {
		return getLowerViewerQualityTier(tier);
	}
	if (performance.factor >= 0.86) {
		const raised = getHigherViewerQualityTier(tier);
		const baseIndex = qualityTierOrder.indexOf(baseTier);
		const raisedIndex = qualityTierOrder.indexOf(raised);
		return qualityTierOrder[Math.min(baseIndex, raisedIndex)];
	}
	return tier;
}

export function resolveAdaptiveViewerQualityState({
	baseTier,
	current,
	performance,
}: {
	baseTier: ResolvedViewerQualityTier;
	current: AdaptiveViewerQualityState | null;
	performance: ViewerPerformanceSample;
}): AdaptiveViewerQualityState | null {
	const currentTier = current?.baseTier === baseTier ? current.tier : null;
	const nextTier = resolveAdaptiveViewerQualityTier({
		baseTier,
		currentTier,
		performance,
	});

	if (!current && nextTier === baseTier) return current;
	if (current?.baseTier === baseTier && current.tier === nextTier)
		return current;

	return {
		baseTier,
		tier: nextTier,
	};
}

// Shared daylight palette: the visible sky and diffuse sky light tell the same story.
const DAYLIGHT_PALETTE = {
	sky: 0x8fadcd,
	horizon: 0xdce1df,
	ground: 0x8d9884,
	sun: 0xffefdb,
};

export function getViewerPresentationProfile(
	tier?: ViewerRuntimeProfile["tier"],
): ViewerPresentationProfile {
	void tier;
	return {
		environment: "studio-soft",
		// Backdrop gradient stops for the authored sky dome (SceneSky).
		// These are DISPLAY colours, not radiance: the dome shader skips
		// the tone-mapping chunk, so what is authored here is what renders.
		// Midday: soft Nordic blue zenith over a pale warm haze band —
		// the haze keeps luminance detail at the horizon instead of the
		// blown-white band the old Preetham sky produced under AgX
		// exposure (issue 101).
		backgroundTop: DAYLIGHT_PALETTE.sky,
		backgroundHorizon: DAYLIGHT_PALETTE.horizon,
		backgroundBottom: DAYLIGHT_PALETTE.ground,
		backgroundAccent: 0xe7b274,
		vignetteStrength: 0.18,
		highlightStrength: 0.28,
		ambientIntensity: 0.16,
		keyIntensity: 1.95,
	};
}

/* ─── Time-of-day curve ────────────────────────────────────────────── */

/**
 * Complete lighting targets for the time slider. Midday inherits the
 * active quality-tier runtime profile; golden hour and night are
 * authored lookdev points. SceneLighting interpolates toward these
 * targets in the demand-mode frame loop.
 */
export interface TimeOfDayLightingTarget {
	ambientIntensity: number;
	hemisphereIntensity: number;
	keyIntensity: number;
	fillIntensity: number;
	rimIntensity: number;
	interiorGlowIntensity: number;
	toneMappingExposure: number;
	ambientColor: number;
	hemisphereTopColor: number;
	hemisphereBottomColor: number;
	keyColor: number;
	fillColor: number;
	rimColor: number;
	interiorGlowColor: number;
	keyPosition: [number, number, number];
	fillPosition: [number, number, number];
	rimPosition: [number, number, number];
	interiorGlowPosition: [number, number, number];
	sunPosition: [number, number, number];
}

function lerpNumber(a: number, b: number, amount: number): number {
	return a + (b - a) * amount;
}

function lerpTuple(
	a: [number, number, number],
	b: [number, number, number],
	amount: number,
): [number, number, number] {
	return [
		lerpNumber(a[0], b[0], amount),
		lerpNumber(a[1], b[1], amount),
		lerpNumber(a[2], b[2], amount),
	];
}

function lerpHexColor(a: number, b: number, amount: number): number {
	const ar = (a >> 16) & 255;
	const ag = (a >> 8) & 255;
	const ab = a & 255;
	const br = (b >> 16) & 255;
	const bg = (b >> 8) & 255;
	const bb = b & 255;
	const r = Math.round(lerpNumber(ar, br, amount));
	const g = Math.round(lerpNumber(ag, bg, amount));
	const blue = Math.round(lerpNumber(ab, bb, amount));
	return (r << 16) | (g << 8) | blue;
}

function mixLightingTarget(
	a: TimeOfDayLightingTarget,
	b: TimeOfDayLightingTarget,
	amount: number,
): TimeOfDayLightingTarget {
	return {
		ambientIntensity: lerpNumber(
			a.ambientIntensity,
			b.ambientIntensity,
			amount,
		),
		hemisphereIntensity: lerpNumber(
			a.hemisphereIntensity,
			b.hemisphereIntensity,
			amount,
		),
		keyIntensity: lerpNumber(a.keyIntensity, b.keyIntensity, amount),
		fillIntensity: lerpNumber(a.fillIntensity, b.fillIntensity, amount),
		rimIntensity: lerpNumber(a.rimIntensity, b.rimIntensity, amount),
		interiorGlowIntensity: lerpNumber(
			a.interiorGlowIntensity,
			b.interiorGlowIntensity,
			amount,
		),
		toneMappingExposure: lerpNumber(
			a.toneMappingExposure,
			b.toneMappingExposure,
			amount,
		),
		ambientColor: lerpHexColor(a.ambientColor, b.ambientColor, amount),
		hemisphereTopColor: lerpHexColor(
			a.hemisphereTopColor,
			b.hemisphereTopColor,
			amount,
		),
		hemisphereBottomColor: lerpHexColor(
			a.hemisphereBottomColor,
			b.hemisphereBottomColor,
			amount,
		),
		keyColor: lerpHexColor(a.keyColor, b.keyColor, amount),
		fillColor: lerpHexColor(a.fillColor, b.fillColor, amount),
		rimColor: lerpHexColor(a.rimColor, b.rimColor, amount),
		interiorGlowColor: lerpHexColor(
			a.interiorGlowColor,
			b.interiorGlowColor,
			amount,
		),
		keyPosition: lerpTuple(a.keyPosition, b.keyPosition, amount),
		fillPosition: lerpTuple(a.fillPosition, b.fillPosition, amount),
		rimPosition: lerpTuple(a.rimPosition, b.rimPosition, amount),
		interiorGlowPosition: lerpTuple(
			a.interiorGlowPosition,
			b.interiorGlowPosition,
			amount,
		),
		sunPosition: lerpTuple(a.sunPosition, b.sunPosition, amount),
	};
}

/* ─── Sun azimuth ──────────────────────────────────────────────────── */

/**
 * Azimuth of the key light / sun disc, in degrees from +Z toward +X
 * (issue 121).
 *
 * The rig used to put the key at ~41°, i.e. the same quadrant as the
 * hero camera, so every cast shadow fell away from the viewer and hid
 * behind the building. Most hero presets look from ~32–35° (square,
 * barrel, classic two-room), so 152° is 120° off them — the lower bound
 * of the 120–140° window the issue specifies. It throws the shadow
 * diagonally across the front-left of frame and puts the key on the
 * camera-visible right facade while the front facade drops to fill
 * light, so the two read as different planes. Two-room's
 * long-entry/panorama hero is the outlier at ~65°, which leaves ~87° —
 * past 90° is what actually matters (the shadow still falls toward the
 * viewer), so that one view reads as a flatter side-light by design.
 *
 * Forest crowns may naturally occlude the low sun disc. The key light's
 * azimuth remains tied to the sauna and camera composition below.
 *
 * `SceneSky` reads the same tuple as `sunPosition` and derives the moon
 * from this constant, so disc, moon and shadow direction cannot drift
 * apart. `viewerConfig.test.ts` re-derives the hero azimuths from the
 * engines' own presets and asserts the offsets, so a camera tweak
 * cannot silently invalidate any of this.
 */
export const SUN_KEY_AZIMUTH_DEG = 152;

/**
 * Re-aim a light position to a compass azimuth, preserving its
 * horizontal radius and height — i.e. its distance and elevation, and
 * therefore the authored shadow length for that time of day.
 */
export function withSunAzimuth(
	position: [number, number, number],
	azimuthDeg: number,
): [number, number, number] {
	const [x, y, z] = position;
	const radius = Math.hypot(x, z);
	const azimuth = (azimuthDeg * Math.PI) / 180;
	return [radius * Math.sin(azimuth), y, radius * Math.cos(azimuth)];
}

function getMiddayLightingTarget(
	runtimeProfile: ViewerRuntimeProfile,
): TimeOfDayLightingTarget {
	// The tier profiles author distance + elevation; the azimuth is the
	// rig's, shared with golden hour and night so the sun never jumps
	// sideways while the time slider is scrubbed.
	const keyPosition = withSunAzimuth(
		runtimeProfile.keyPosition,
		SUN_KEY_AZIMUTH_DEG,
	);
	return {
		ambientIntensity: runtimeProfile.ambientIntensity,
		hemisphereIntensity: runtimeProfile.hemisphereIntensity,
		keyIntensity: runtimeProfile.keyIntensity,
		fillIntensity: runtimeProfile.fillIntensity,
		rimIntensity: runtimeProfile.rimIntensity,
		interiorGlowIntensity: runtimeProfile.interiorGlowIntensity,
		toneMappingExposure: runtimeProfile.toneMappingExposure,
		ambientColor: 0xf8f3ec,
		hemisphereTopColor: DAYLIGHT_PALETTE.sky,
		hemisphereBottomColor: 0x7c8b76,
		keyColor: DAYLIGHT_PALETTE.sun,
		fillColor: 0xd9edf5,
		rimColor: 0xffd8a6,
		interiorGlowColor: 0xffc47b,
		keyPosition,
		fillPosition: runtimeProfile.fillPosition,
		rimPosition: runtimeProfile.rimPosition,
		interiorGlowPosition: runtimeProfile.interiorGlowPosition,
		sunPosition: keyPosition,
	};
}

function getGoldenHourLightingTarget(
	runtimeProfile: ViewerRuntimeProfile,
): TimeOfDayLightingTarget {
	// 12.2 m out at 2.15 m up → ~10° elevation, i.e. a ~12 m shadow off
	// a 2.2 m sauna. That length is what made the missing frustum so
	// visible, and it is the worst case the shadow-camera fit is sized
	// against (see sunShadowFrustum.ts).
	const keyPosition = withSunAzimuth([9.4, 2.15, 7.8], SUN_KEY_AZIMUTH_DEG);
	return {
		ambientIntensity: Math.max(runtimeProfile.ambientIntensity * 0.82, 0.11),
		hemisphereIntensity: Math.max(
			runtimeProfile.hemisphereIntensity * 0.74,
			0.18,
		),
		keyIntensity: runtimeProfile.keyIntensity * 0.72,
		fillIntensity: runtimeProfile.fillIntensity * 0.52,
		rimIntensity: Math.max(runtimeProfile.rimIntensity * 1.38, 0.72),
		interiorGlowIntensity: runtimeProfile.interiorGlowIntensity * 1.2,
		toneMappingExposure: runtimeProfile.toneMappingExposure + 0.05,
		ambientColor: 0xffd4b0,
		hemisphereTopColor: 0xf7b47f,
		hemisphereBottomColor: 0x566955,
		keyColor: 0xffae62,
		fillColor: 0x8fa6c9,
		rimColor: 0xffd3a1,
		interiorGlowColor: 0xffb56f,
		keyPosition,
		fillPosition: [-5.6, 3.1, 4.4],
		rimPosition: [-6.2, 3.4, -7.8],
		interiorGlowPosition: runtimeProfile.interiorGlowPosition,
		sunPosition: keyPosition,
	};
}

function getNightLightingTarget(
	runtimeProfile: ViewerRuntimeProfile,
): TimeOfDayLightingTarget {
	// Night key is moonlight: same azimuth as the day sun so the shadow
	// direction stays continuous across the whole slider, only weaker
	// and cooler. The sun itself has set — same plan direction, below
	// the horizon — which is why `sunPosition` keeps its negative Y.
	const keyPosition = withSunAzimuth([4.2, 0.75, 5.8], SUN_KEY_AZIMUTH_DEG);
	const sunPosition = withSunAzimuth([4.2, -1.2, 5.8], SUN_KEY_AZIMUTH_DEG);
	return {
		ambientIntensity: Math.max(runtimeProfile.ambientIntensity * 0.62, 0.09),
		hemisphereIntensity: Math.max(
			runtimeProfile.hemisphereIntensity * 0.58,
			0.16,
		),
		keyIntensity: runtimeProfile.keyIntensity * 0.28,
		fillIntensity: runtimeProfile.fillIntensity * 0.28,
		rimIntensity: Math.max(runtimeProfile.rimIntensity * 0.78, 0.36),
		interiorGlowIntensity: Math.max(
			runtimeProfile.interiorGlowIntensity * 2.9,
			1.18,
		),
		toneMappingExposure: Math.max(
			runtimeProfile.toneMappingExposure - 0.13,
			0.82,
		),
		ambientColor: 0x242b55,
		hemisphereTopColor: 0x162850,
		hemisphereBottomColor: 0x223047,
		keyColor: 0x8fa8ff,
		fillColor: 0x475482,
		rimColor: 0xaebdff,
		interiorGlowColor: 0xffb46f,
		keyPosition,
		fillPosition: [-4.8, 2.4, 3.4],
		rimPosition: [-4.2, 4.8, -7.2],
		interiorGlowPosition: runtimeProfile.interiorGlowPosition,
		sunPosition,
	};
}

export function getTimeOfDayLightingTarget(
	timeOfDay: TimeOfDayValue | LegacyTimeOfDay,
	runtimeProfile: ViewerRuntimeProfile,
): TimeOfDayLightingTarget {
	const value = normalizeTimeOfDay(timeOfDay);
	const midday = getMiddayLightingTarget(runtimeProfile);
	const golden = getGoldenHourLightingTarget(runtimeProfile);
	const night = getNightLightingTarget(runtimeProfile);
	if (value <= GOLDEN_HOUR_TIME_OF_DAY) {
		return mixLightingTarget(midday, golden, value / GOLDEN_HOUR_TIME_OF_DAY);
	}
	return mixLightingTarget(
		golden,
		night,
		(value - GOLDEN_HOUR_TIME_OF_DAY) /
			(NIGHT_TIME_OF_DAY - GOLDEN_HOUR_TIME_OF_DAY),
	);
}

export interface TimeOfDayPresentationTarget {
	/** Sky-dome zenith colour (authored display colour, not radiance). */
	backgroundTop: number;
	/** Sky-dome horizon-band colour; also tints the distance fog. */
	backgroundHorizon: number;
	/** Sky-dome colour below the horizon line (meets the ground plane). */
	backgroundBottom: number;
	backgroundAccent: number;
	vignetteStrength: number;
	highlightStrength: number;
	sunPosition: [number, number, number];
	starOpacity: number;
	moonOpacity: number;
	/**
	 * Vertical extent of the horizon→zenith blend band (in view-direction
	 * Y, 0..1). Wider reads as hazy summer air; tighter reads as clear
	 * night air.
	 */
	horizonSoftness: number;
	/** Colour of the sun halo painted onto the dome. */
	sunGlowColor: number;
	/** Strength of the sun halo. Peaks at golden hour. */
	sunGlowIntensity: number;
}

export function getTimeOfDayPresentationTarget(
	timeOfDay: TimeOfDayValue | LegacyTimeOfDay,
	presentationProfile: ViewerPresentationProfile,
	runtimeProfile: ViewerRuntimeProfile,
): TimeOfDayPresentationTarget {
	const lighting = getTimeOfDayLightingTarget(timeOfDay, runtimeProfile);
	const value = clampTimeOfDay(normalizeTimeOfDay(timeOfDay));
	const nightFade = Math.max(0, (value - 0.72) / 0.28);
	const goldenBlend =
		value <= GOLDEN_HOUR_TIME_OF_DAY
			? value / GOLDEN_HOUR_TIME_OF_DAY
			: 1 - (value - GOLDEN_HOUR_TIME_OF_DAY) / 0.3;
	const nightBlend = Math.min(1, Math.max(0, nightFade));
	const warmHorizon = lerpHexColor(
		presentationProfile.backgroundHorizon,
		0xf3a15f,
		Math.max(0, goldenBlend),
	);
	return {
		// Zenith: Nordic blue → dusk blue-violet at golden hour (a real
		// sunset sky stays cool overhead while the horizon warms) → navy
		// night.
		backgroundTop: lerpHexColor(
			lerpHexColor(presentationProfile.backgroundTop, 0x7484ab, goldenBlend),
			0x162248,
			nightBlend,
		),
		backgroundHorizon: lerpHexColor(warmHorizon, 0x3d355f, nightBlend),
		// Below-horizon: sage haze → warm earth glow at golden hour →
		// near-black night ground.
		backgroundBottom: lerpHexColor(
			lerpHexColor(presentationProfile.backgroundBottom, 0xa08066, goldenBlend),
			0x111829,
			nightBlend,
		),
		backgroundAccent: lerpHexColor(
			presentationProfile.backgroundAccent,
			0xf2a36a,
			Math.max(goldenBlend, nightBlend * 0.6),
		),
		vignetteStrength: lerpNumber(
			presentationProfile.vignetteStrength,
			0.34,
			nightBlend,
		),
		highlightStrength: lerpNumber(
			presentationProfile.highlightStrength,
			0.55,
			Math.max(goldenBlend, nightBlend),
		),
		sunPosition: lighting.sunPosition,
		starOpacity: Math.max(0, (value - 0.78) / 0.22),
		moonOpacity: Math.max(0, (value - 0.74) / 0.26),
		// Hazy summer air at midday, clearer (tighter band) at night.
		horizonSoftness: lerpNumber(0.42, 0.26, nightBlend),
		// Near-white day sun (a noon halo must not read as sunset) →
		// saturated golden-hour halo → faint cool glow once the sun has
		// set (the moon mesh carries the night).
		sunGlowColor: lerpHexColor(
			lerpHexColor(0xfff9ee, 0xffa554, goldenBlend),
			0x8fa8ff,
			nightBlend,
		),
		sunGlowIntensity: lerpNumber(
			lerpNumber(0.2, 1.0, goldenBlend),
			0.1,
			nightBlend,
		),
	};
}
